/*!
 * resources.js — resources(): a tiny HUD of how hard the machine is working
 * © 2026 Rob Canning — GPLv3
 *
 *     resources()            show it (again: hide it)
 *     resources(at:tl)       in a corner: tl, tr, bl, br (br)
 *     resources(stop)        hide it
 *
 *   cpu    the machine, all cores (and the busiest core: the audio can max
 *          one out long before the total looks high) -- from the server,
 *          which runs on the same machine
 *   mem    the machine's memory in use
 *   fps    frames the page draws a second
 *   audio  ok, or late: the audio clock falling behind real time -- the
 *          sound is dropping out
 *   voices the sounds running (synth and audio voices)
 *   paused sounds let go with gate(s, 0): silent, still running (amber)
 *   bangs  bang(s, every) metros going
 *   heap   the page's own memory (Chrome)
 *
 * Each turns amber, then red, as it nears its limit. Once a second. Not on
 * the projector window (follow=1).
 */

import { visibleBand } from "./viewBand.js";

const POLL_MS = 1000;
let hud = null, timer = 0, raf = 0, frames = 0, lastT = 0;
let lastTs = null;                 // the audio clock against real time, at the last look
let longTasks = 0, observer = null;

const pct = v => (v == null || !Number.isFinite(v) ? "–" : `${Math.round(v * 100)}%`);
/** level 0 ok, 1 amber, 2 red: high is bad (or low is bad, with low:true). */
const grade = (v, amber, red, low = false) => (v == null ? 0 : low ? (v < red ? 2 : v < amber ? 1 : 0) : (v > red ? 2 : v > amber ? 1 : 0));
const COLORS = ["", "#e8b04a", "#ff5a4a"];

function css() {
  if (document.getElementById("oscilla-resources-css")) return;
  const st = document.createElement("style");
  st.id = "oscilla-resources-css";
  st.textContent = `
.oscilla-resources { position: fixed; z-index: 26000; pointer-events: none;
  font: 14px/1.4 ui-monospace, Menlo, monospace; color: #cfd6dd;
  background: rgba(10, 12, 14, 0.55); border-radius: 4px; padding: 4px 9px;
  white-space: nowrap; letter-spacing: 0.02em; }
.oscilla-resources b { font-weight: 400; color: #8a949e; }
.oscilla-resources.tl, .oscilla-resources.bl { left: 10px; }
.oscilla-resources.tr, .oscilla-resources.br { right: 10px; }`;
  document.head.appendChild(st);
}

/**
 * Into its corner of the band the score shows through -- between the top
 * bar and the transport (viewBand.js), whatever their size -- not behind them.
 */
function place() {
  if (!hud) return;
  const container = document.getElementById("scoreContainer") || document.documentElement;
  const cr = container.getBoundingClientRect();
  const [b0, b1] = visibleBand(cr);
  const top = hud.classList.contains("tl") || hud.classList.contains("tr");
  hud.style.top = top ? `${Math.round(cr.top + b0 + 8)}px` : "";
  hud.style.bottom = top ? "" : `${Math.round(window.innerHeight - (cr.top + b1) + 8)}px`;
}

function countFrames(t) {
  frames++;
  raf = requestAnimationFrame(countFrames);
}

async function sample() {
  const now = performance.now();
  const secs = lastT ? (now - lastT) / 1000 : 1;
  const fps = lastT ? frames / secs : null;
  frames = 0; lastT = now;

  // the audio clock against real time: behind is dropping out
  let audio = "–", audioGrade = 0;
  const ctx = window.sharedAudioCtx;
  if (ctx?.getOutputTimestamp && ctx.state === "running") {
    const ts = ctx.getOutputTimestamp();
    if (lastTs && ts.performanceTime > lastTs.performanceTime) {
      const ratio = (ts.contextTime - lastTs.contextTime) / ((ts.performanceTime - lastTs.performanceTime) / 1000);
      audio = ratio > 0.985 ? "ok" : "late";
      audioGrade = ratio > 0.985 ? 0 : ratio > 0.95 ? 1 : 2;
    }
    lastTs = ts;
  } else if (ctx) { audio = ctx.state; lastTs = null; }

  const voices = (window.activeSynthVoices?.size || 0) + (window.activeAudioCues?.size || 0);
  // sounds paused (gate(s, 0)): running silent, still costing
  let paused = 0, metros = 0;
  try { const st = (await import("../cues/synth.js")).soundStates(); paused = st.gated.length; metros = st.banging.length; } catch { /* none */ }
  const heap = performance.memory?.usedJSHeapSize;
  const jank = longTasks; longTasks = 0;

  let sys = null;
  try { sys = await (await fetch("/api/system", { cache: "no-store" })).json(); } catch { /* no server: the page's own */ }
  if (!hud) return;

  const cell = (label, text, g) => `<b>${label}</b> <span style="color:${COLORS[g]}">${text}</span>`;
  const parts = [];
  if (sys) {
    parts.push(cell("cpu", `${pct(sys.cpu)}${sys.core != null ? ` (core ${pct(sys.core)})` : ""}`, Math.max(grade(sys.cpu, 0.7, 0.9), grade(sys.core, 0.85, 0.97))));
    parts.push(cell("mem", pct(sys.mem), grade(sys.mem, 0.8, 0.92)));
  }
  parts.push(cell("fps", fps == null ? "–" : Math.round(fps), grade(fps, 45, 25, true)));
  parts.push(cell("audio", audio, audioGrade));
  parts.push(cell("voices", voices, grade(voices, 40, 80)));
  if (paused) parts.push(cell("paused", paused, 1));
  if (metros) parts.push(cell("bangs", metros, 0));
  if (heap) parts.push(cell("heap", `${Math.round(heap / 1048576)}M`, grade(heap / (performance.memory.jsHeapSizeLimit || Infinity), 0.6, 0.85)));
  if (jank) parts.push(cell("stalls", jank, jank > 3 ? 2 : 1));
  hud.innerHTML = parts.join("  ");
  place();                          // the bars may have moved since
}

export function showResources(at = "br") {
  if (new URLSearchParams(location.search).get("follow") === "1") return;   // not on the projector
  css();
  if (!hud) {
    hud = document.createElement("div");
    document.body.appendChild(hud);
    raf = requestAnimationFrame(countFrames);
    timer = setInterval(sample, POLL_MS);
    try {
      observer = new PerformanceObserver(list => { longTasks += list.getEntries().length; });
      observer.observe({ type: "longtask", buffered: false });
    } catch { observer = null; }
    hud.textContent = "…";
    sample();
  }
  hud.className = `oscilla-resources ${["tl", "tr", "bl", "br"].includes(at) ? at : "br"}`;
  place();
}

export function hideResources() {
  clearInterval(timer); cancelAnimationFrame(raf);
  observer?.disconnect(); observer = null;
  hud?.remove(); hud = null;
  lastT = 0; lastTs = null; frames = 0;
}

export const resourcesShown = () => !!hud;

/** resources(...) as a cue. */
export function handleResourcesCue(ast) {
  const opts = {};
  for (const a of ast?.args || []) opts[a.type] = a.value;
  if ("stop" in opts || "off" in opts) return hideResources();
  if (hud && opts.at == null) return hideResources();          // again: hide
  showResources(String(opts.at ?? "br"));
}
